import { Injectable, inject } from '@angular/core';

import { Observable, forkJoin, of, shareReplay, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { ApiService } from '@core/services/api.service';
import { CompanyApiService } from '@core/services/company-api.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { MockDataService } from '@core/services/mock-data.service';
import { TermsService } from '@core/services/terms.service';

import {
  DispositionPreferenceLabel,
  PaymentMethod,
  PaymentMethodLabel,
  Tax1099Status,
  Tax1099StatusLabel,
  TaxEntityTypeLabel
} from '../models/profile.enum';
import { formatMailingAddress, mapConsignorProfileHttpResponseToState } from '../models/profile.mapper';
import {
  ChangePasswordRequest,
  SubmitTaxInfoRequest,
  UpdateNotificationPreferencesRequest,
  UpdatePaymentMethodRequest,
  UpdateProfileRequest,
  UpdateUsernameRequest
} from '../models/profile.request';
import { ConsignorProfileHttpResponse } from '../models/profile.response';
import { ConsignorAddressState, ConsignorProfileState } from '../models/profile.state';

/**
 * Feature service for the consignor profile.
 * Mock branch reads `MockDataService` and caches the active consignor's
 * profile (keyed by consignor id) so submitted updates survive navigation
 * without being leaked across consignors when the dev auth user changes.
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly api = inject(ApiService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly mockData = inject(MockDataService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly terms = inject(TermsService);

  private readonly profileCache = new Map<string, ConsignorProfileState>();
  private _liveProfile$: Observable<ConsignorProfileState> | null = null;

  getProfile(): Observable<ConsignorProfileState> {
    if (!this._liveProfile$) {
      // The profile endpoint carries no T&C data — that lives on the separate
      // Terms gate endpoint, so both are fetched and merged together.
      this._liveProfile$ = forkJoin({
        profile: this.api.get<ConsignorProfileHttpResponse>('ConsignorMe/Profile'),
        gate: this.terms.getGate()
      }).pipe(
        map(({ profile, gate }) => mapConsignorProfileHttpResponseToState(profile, gate)),
        tap(() => {
          this._liveProfile$ = null;
        }),
        shareReplay(1)
      );
    }
    return this._liveProfile$;
  }

  invalidateProfile(): void {
    this._liveProfile$ = null;
  }

  submitTaxInfo(request: SubmitTaxInfoRequest): Observable<ConsignorProfileState> {
    if (environment.useMockData) {
      const id = this.consignorIdOrThrow();
      if (id instanceof Error) return throwError(() => id);
      const profile = this.readMockProfile(id);
      const last4 = request.taxId.replace(/[^0-9]/g, '').slice(-4);
      // Belt-and-braces: scrub the raw TIN from the closure-retained request
      // so downstream logging/error wrapping can never capture the full value.
      request.taxId = '';
      const next: ConsignorProfileState = {
        ...profile,
        tax: {
          ...profile.tax,
          status: Tax1099Status.TaxInfoComplete,
          statusLabel: Tax1099StatusLabel[Tax1099Status.TaxInfoComplete],
          taxEntityTypeLabel: TaxEntityTypeLabel[request.taxEntityType],
          legalName: request.legalName,
          taxIdLast4: last4,
          mailingAddress: request.mailingAddress,
          isComplete: true
        }
      };
      this.profileCache.set(id, next);
      this.currentUser.setTax1099Status(Tax1099Status.TaxInfoComplete);
      return of(next);
    }
    return throwError(() => new Error('Profile API not implemented — run with useMockData=true.'));
  }

  /**
   * Single write for the account form — phone, address and disposition together
   * (`PUT ConsignorMe/Profile`). The real branch re-fetches the full profile so
   * the returned state carries the merged T&C gate the update endpoint omits.
   */
  updateProfile(request: UpdateProfileRequest): Observable<ConsignorProfileState> {
    if (environment.useMockData) {
      const id = this.consignorIdOrThrow();
      if (id instanceof Error) return throwError(() => id);
      const profile = this.readMockProfile(id);
      const trimmedPhone = request.phoneNumber.trim();
      const phoneChanged = trimmedPhone !== profile.phoneNumber;
      const address: ConsignorAddressState = {
        line1: request.address.line1.trim(),
        line2: request.address.line2?.trim() || undefined,
        city: request.address.city.trim(),
        stateName: request.address.stateName.trim(),
        countryName: request.address.countryName.trim(),
        zipCode: request.address.zipCode.trim()
      };
      const next: ConsignorProfileState = {
        ...profile,
        phoneNumber: trimmedPhone,
        address,
        mailingAddress: formatMailingAddress(address),
        dispositionPreference: request.dispositionPreference,
        dispositionPreferenceLabel: DispositionPreferenceLabel[request.dispositionPreference],
        // FR §6.3 / §10 — stamp a fresh verifiedAt whenever the number changes.
        phoneVerified: true,
        phoneVerifiedAt: phoneChanged ? new Date().toISOString() : profile.phoneVerifiedAt
      };
      this.profileCache.set(id, next);
      this.mockData.updateConsignor(id, next);
      return of(next);
    }

    const body = {
      phoneNumber: request.phoneNumber.trim(),
      dispositionPreference: request.dispositionPreference,
      address: {
        line1: request.address.line1.trim(),
        line2: request.address.line2?.trim() || null,
        city: request.address.city.trim(),
        zipCode: request.address.zipCode.trim(),
        stateName: request.address.stateName.trim(),
        countryName: request.address.countryName.trim()
      }
    };
    return this.api.put<ConsignorProfileHttpResponse>('ConsignorMe/Profile', body).pipe(
      switchMap(() => {
        this.invalidateProfile();
        return this.getProfile();
      })
    );
  }

  updatePaymentMethod(request: UpdatePaymentMethodRequest): Observable<ConsignorProfileState> {
    if (environment.useMockData) {
      const id = this.consignorIdOrThrow();
      if (id instanceof Error) return throwError(() => id);
      const profile = this.readMockProfile(id);
      const next: ConsignorProfileState = {
        ...profile,
        payment: {
          method: request.method,
          methodLabel: PaymentMethodLabel[request.method],
          maskedBankDetails: request.method === PaymentMethod.Ach ? maskLast4(request.bankAccountNumber) : undefined,
          maskedCheckAddress:
            request.method === PaymentMethod.Check ? maskCheckAddress(request.mailingAddress) : undefined,
          storeCreditBalance:
            request.method === PaymentMethod.StoreCredit ? profile.payment.storeCreditBalance : undefined
        }
      };
      this.profileCache.set(id, next);
      return of(next);
    }
    return throwError(() => new Error('Profile API not implemented — run with useMockData=true.'));
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    const userId = this.currentUser.userId();
    return this.companyApi.changePassword(userId, {
      id: Number(userId),
      oldPassword: request.currentPassword,
      newPassword: request.newPassword,
      conformPassVal: request.confirmPassword
    });
  }

  updateUsername(request: UpdateUsernameRequest): Observable<string> {
    const userId = this.currentUser.userId();
    return this.companyApi.updateUsername(userId, request.username).pipe(map(res => res.userName ?? request.username));
  }

  updateNotifications(request: UpdateNotificationPreferencesRequest): Observable<ConsignorProfileState> {
    if (environment.useMockData) {
      const id = this.consignorIdOrThrow();
      if (id instanceof Error) return throwError(() => id);
      const profile = this.readMockProfile(id);
      const overrides = new Map(request.updates.map(u => [u.event, u.enabled]));
      const next: ConsignorProfileState = {
        ...profile,
        notifications: profile.notifications.map(n =>
          n.configurable && overrides.has(n.event) ? { ...n, enabled: overrides.get(n.event) ?? n.enabled } : n
        )
      };
      this.profileCache.set(id, next);
      return of(next);
    }
    return throwError(() => new Error('Profile API not implemented — run with useMockData=true.'));
  }

  /** Dev-only: reset the in-memory profile cache (used by tests + dev login swaps). */
  clearCache(): void {
    this.profileCache.clear();
  }

  private readMockProfile(consignorId: string): ConsignorProfileState {
    const cached = this.profileCache.get(consignorId);
    if (cached) return cached;
    const seed = this.mockData.getConsignor(consignorId);
    if (!seed) {
      throw new Error(`No consignor found for id ${consignorId}.`);
    }
    const snapshot: ConsignorProfileState = { ...seed };
    this.profileCache.set(consignorId, snapshot);
    return snapshot;
  }

  private consignorIdOrThrow(): string | Error {
    const id = this.currentUser.consignorId();
    if (!id) return new Error('Profile requires an authenticated consignor.');
    return id;
  }
}

function maskLast4(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const digits = value.replace(/[^0-9]/g, '');
  if (digits.length < 4) return undefined;
  return `****${digits.slice(-4)}`;
}

function maskCheckAddress(address: string | undefined): string | undefined {
  const trimmed = address?.trim();
  if (!trimmed) return undefined;
  // Swap the last 2 digits of the zip for asterisks so the masked view echoes
  // the real mock shape (`123 Main St, Seattle WA 98***`).
  return trimmed.replace(/(\d{2})$/, '***');
}
